import { FC, useRef, useCallback, ChangeEvent, DragEvent } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import InputText from 'modules/_shared/components/InputText';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import { useAcceptIncentiveOfferModal } from '../context/AcceptIncentiveOfferModalContext';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const AcceptIncentiveOfferModalStep2: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAcceptIncentiveOfferModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const partnerAddress = data.beneficiaryBuyer?.address;

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        Array.from(files).forEach((file) => actions.handleAddDocument(file));
      }
      e.target.value = '';
    },
    [actions]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { files } = e.dataTransfer;
      if (files) {
        Array.from(files).forEach((file) => actions.handleAddDocument(file));
      }
    },
    [actions]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-2">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('DataVerification')}</h5>
        <p className="text-soft">{t('ConfirmYourDataToCompleteOperation')}</p>
      </div>

      <div className="row">
        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('PersonalData')}</h6>
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="accept-offer-buyer-name"
            label={t('FullName')}
            value={data.beneficiaryBuyer?.name || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerField('name', value)
            }
            required
            lgSize={false}
            tooltip={t('FullNameAsInIdentityDocument')}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="accept-offer-buyer-cif"
            label={t('NifNie')}
            value={data.beneficiaryBuyer?.cif || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerField('cif', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="accept-offer-buyer-email"
            label={t('Email')}
            value={data.beneficiaryBuyer?.email || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerField('email', value)
            }
            required
            lgSize={false}
            emailInput
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="accept-offer-buyer-phone"
            label={t('Telephone')}
            value={data.beneficiaryBuyer?.phone || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerField('phone', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('FiscalResidence')}</h6>
        </div>

        <div className="col-12 mb-3">
          <InputText
            id="accept-offer-buyer-address-line1"
            label={t('FullAddress')}
            value={partnerAddress?.line1 || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerAddressField('line1', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="accept-offer-buyer-address-zip"
            label={t('PostalCode')}
            value={partnerAddress?.zip || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerAddressField('zip', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="accept-offer-buyer-address-city"
            label={t('City')}
            value={partnerAddress?.city || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerAddressField('city', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="accept-offer-buyer-address-country"
            label={t('Country')}
            value={partnerAddress?.country || ''}
            onChange={(value) =>
              actions.handleUpdatePartnerBuyerAddressField('country', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('Documentation')}</h6>
        </div>

        <div className="col-12">
          <div className="form-group">
            <label className="form-label d-flex align-items-center">
              {t('AttachIdentityDocument')}
              <MandatoryField className="ml-1" />

              <IconTooltip
                id="accept-offer-identity-doc-tooltip"
                tooltip={t('IdentityDocumentTooltip')}
                place="right"
                className="fs-24px text-soft"
              />
            </label>
            <div
              className="upload-zone border border-dashed rounded p-4 text-center dropzone dz-clickable"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUploadClick();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <em className="icon ni ni-upload-cloud fs-24px text-soft" />
              <p className="text-soft mb-2">{t('DragDocumentHereOr')}</p>
              <button
                type="button"
                className="btn btn-dim btn-sm btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadClick();
                }}
              >
                {t('SelectFile')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileSelect}
              />
              <p className="text-soft fs-11px mt-2 mb-0">
                {t('IdentityDocumentFormats')}
              </p>
            </div>

            {data.buyerDocuments.length > 0 && (
              <div className="mt-2">
                {data.buyerDocuments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="d-flex align-items-center justify-content-between py-1 px-2 bg-light rounded mb-1"
                  >
                    <span className="text-soft fs-12px text-truncate flex-grow-1 mr-2">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-outline-danger"
                      onClick={() => actions.handleRemoveDocument(index)}
                      aria-label={t('Remove')}
                    >
                      <em className="icon ni ni-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 mt-2">
          <InputCheckbox
            id="verify-data-checkbox"
            label={t('ConfirmDataIsCorrect')}
            checked={data.isDataVerified}
            onChange={actions.handleToggleDataVerified}
          />
        </div>
      </div>
    </div>
  );
};

export default AcceptIncentiveOfferModalStep2;
